import { Map, fromJS } from 'immutable'
import _isNil from 'lodash/isNil'
import _isEmpty from 'lodash/isEmpty'
import { DEFAULT_LANGUAGE } from 'services/languages/constants'
import * as actions from './actions'
import { getSafeNodeId, isValidNodeId } from './utils'

export const initialState = Map()

/**
 * Test if the key is a valid node key
 * @param {*} id The id to test
 * @returns {Boolean} True if this is a valid node key
 */
function isValidNodeKey (key) {
  return !_isEmpty(key)
}

export function nodeReducer (state = initialState, action) {
  const { type, payload = {} } = action
  switch (type) {
    case actions.GET_PM_MULTIPLE_NODES: {
      const { key, ids, language = DEFAULT_LANGUAGE } = payload
      if (!isValidNodeKey(key)) {
        throw new Error('invalid node key: ', key, '. Should be any valid indentifier')
      }
      return state.withMutations((mutableState) => {
        ids.forEach((id) => {
          const processing = mutableState.getIn([key, getSafeNodeId(id), language, 'processing'], false)
          const data = mutableState.getIn([key, getSafeNodeId(id), language, 'data'], null)
          const isNotFetched = processing === false && !data

          if (isNotFetched) {
            mutableState.setIn([key, getSafeNodeId(id), language, 'processing'], true)
            mutableState.setIn([key, getSafeNodeId(id), language, 'data'], null)
          }
        })
      })
    }

    case actions.NODES_RECIEVED: {
      const { key, nodes, language = DEFAULT_LANGUAGE } = payload
      if (!isValidNodeKey(key)) {
        throw new Error('action should have a valid key')
      }

      return state.withMutations((mutableState) => {
        nodes.forEach((node) => {
          const nodeId = node.id
          if (!isValidNodeId(nodeId)) {
            return
          }

          mutableState.setIn([key, getSafeNodeId(nodeId), language, 'processing'], false)
          mutableState.setIn([key, getSafeNodeId(nodeId), language, 'id'], getSafeNodeId(nodeId))

          const nodeError = node.error
          if (nodeError) {
            mutableState.setIn([key, getSafeNodeId(nodeId), language, 'data'], null)
            mutableState.setIn([key, getSafeNodeId(nodeId), language, 'error'], fromJS(nodeError))
          } else {
            const nodeContent = node.data
            mutableState.setIn([key, getSafeNodeId(nodeId), language, 'data'], fromJS(nodeContent))
            mutableState.setIn([key, getSafeNodeId(nodeId), language, 'error'], null)
          }
        })
      })
    }
    case actions.REMOVE_NODES: {
      const { keys } = payload
      if (keys) { return keys.reduce((acc, curr) => acc.delete(curr), state) }

      return state
    }

    case actions.NODE_IMPRESSED: {
      const { id, language, key } = action.payload
      if (_isNil(id)) {
        throw new Error(
          `${actions.NODE_IMPRESSED} requires valid id parameter, but got: ${id}`,
        )
      }

      if (_isNil(language)) {
        throw new Error(`${actions.NODE_IMPRESSED} requires valid language parameter, but got: ${language}`)
      }

      if (_isNil(key)) {
        throw new Error(`${actions.NODE_IMPRESSED} requires valid key parameter, but got: ${key}`)
      }
      return state.withMutations((mutableState) => {
        const node = mutableState.getIn([key, getSafeNodeId(id)])
        if (!_isNil(node)) {
          mutableState
            .setIn([key, getSafeNodeId(id), language, 'processing'], false)
            .setIn([key, getSafeNodeId(id), language, 'impressed'], true)
        }
      })
    }
    case actions.CLEAR_ALL_NODES_IMPRESSION: {
      const { key } = action.payload

      const stateByKey = state.get(key)
      if (!Map.isMap(stateByKey)) {
        return state
      }
      return state.withMutations((mutableState) => {
        mutableState.get(key).mapKeys((id, langMap) => {
          langMap.mapKeys((lang, item) => {
            if (item.get('impressed')) {
              mutableState.setIn([key, getSafeNodeId(id), lang, 'impressed'], false)
            }
          })
        })
      })
    }
    case actions.RESET_NODES_BY_KEY: {
      const { key } = action.payload

      return state.delete(key)
    }
    default:
      return state
  }
}

export default nodeReducer
